import { Row, Col, Card, Spin, Avatar, Image, Badge, Space, Button } from "antd";
import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import { ArrowLeftIcon } from "lucide-react";

function View() {

  const sectionName     =   "User";
  const routeName       =   "user";

  const params                =   useParams();
  const location              =   useLocation();
  const { request }           =   useRequest();
  const [list, setList]       =   useState({});
  const [loading, setLoading] =   useState(false);

  const fetchData = (id) => {
    setLoading(true);
    request({
      url: `${apiPath.viewUser}/${id}`,
      method: "GET",
      onSuccess: (response) => {
        setList(response.data.userInfo);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(params.id);
  }, []);

  const renderValue = (key, value) => {
    if (["otp_verify_at", "created_at", "updated_at"].includes(key))
      return value ? moment(value).format("DD-MMM-YYYY HH:mm") : "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value ?? "-";
  };

  return (
    <Spin spinning={loading} size="large">
      <Row gutter={[16, 16]}>
       
        <Col span={24}>
          <Card
            title={`${sectionName} Profile`}
            variant={false}
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Row align="middle" gutter={24}>
              <Col xs={24} sm={6} md={4} className="text-center">
                {list?.image ? (
                  <Image
                    src={`${apiPath.assetURL}${list.image}`}
                    width={120}
                    height={120}
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                  />
                ) : (
                  <Avatar
                    size={120}
                    style={{
                      fontSize: 40,
                      backgroundColor: "#1677ff",
                    }}
                  >
                    {list?.name?.charAt(0)?.toUpperCase() ?? "U"}
                  </Avatar>
                )}
              </Col>

              <Col xs={24} sm={18} md={20}>
                <div className="d-flex justify-between">
                  <div>
                    <h2 className="font-semibold mb-1">
                      {list?.name?.toUpperCase() ?? "Anonymous User"}
                    </h2>

                    <p className="text-muted mb-1">
                      {list?.email ?? "Email not available"}
                    </p>

                    <p className="text-muted">
                      {list?.country_code ? `+${list.country_code}-` : ""}
                      {list?.phone ?? "-"}
                    </p>
                  </div>

                  <div>
                    {list?.otp && (
                      <Button>
                        {list?.otp ? `OTP: ${list.otp}` : "No OTP"}
                      </Button>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Basic Info */}
        <Col span={24} md={12}>
          <Card title="Basic Information" variant={false}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              
              <div className="view-inner-cls">
                <h5>User Type:</h5>
                <h6>{renderValue("user_type", list.user_type)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Language:</h5>
                {renderValue("language", list.language)}
              </div>

              <div className="view-inner-cls">
                <h5>Salesman Code:</h5>
                {renderValue("salesman_code", list.salesman_code)}
              </div>

              <div className="view-inner-cls">
                <h5>Business Nature:</h5>
                {renderValue("business_nature", list.business_nature)}
              </div>

              <div className="view-inner-cls">
                <h5>Current Segment:</h5>
                <h6>
                  {list?.current_segment_id?.en_name
                    ? list.current_segment_id.en_name
                    : renderValue("current_segment_id", list.current_segment_id)}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Segments:</h5>
                <h6>
                  {list?.segments?.map((item, index) => (
                    <span key={item._id}>
                      {item?.en_name}
                      {index < list?.segments?.length - 1 && ', '}
                    </span>
                  ))}
                </h6>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Contact Details */}
        <Col span={24} md={12}>
          <Card
            title="Contact Details"
            variant={false}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>

              <div className="view-inner-cls">
                <h5>Contact Name:</h5>
                <h6>{renderValue("contact_name", list.contact_name)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Alt. Phone:</h5>
                <h6>{renderValue("alterntive_phone", list.alterntive_phone)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Landline:</h5>
                <h6>{renderValue("landline_phone", list.landline_phone)}</h6>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Address */}
        <Col span={24} md={12}>
          <Card
            title="Address Information"
            variant={false}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div className="view-inner-cls">
                <h5>Address 1:</h5>
                <h6>{renderValue("address_line_1", list.address_line_1)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Address 2:</h5>
                <h6>{renderValue("address_line_2", list.address_line_2)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Area:</h5>
                <h6>{renderValue("area", list.area)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>City:</h5>
                <h6>
                  {list?.city?.en_name
                    ? list.city.en_name
                    : renderValue("city", list.city)}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>State:</h5>
                <h6>
                  {list?.state?.en_name
                    ? list.state.en_name
                    : renderValue("state", list.state)}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Pincode:</h5>
                <h6>{renderValue("pincode", list.pincode)}</h6>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Account Info */}
        <Col span={24} md={12}>
          <Card
            title="Account Status"
            variant={false}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div className="view-inner-cls">
                <h5>Profile Completed:</h5>
                <h6>
                  {list?.is_profile_completed ? (
                    <Badge status="success" text="Completed" />
                  ) : (
                    <Badge status="error" text="Not yet" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Active:</h5>
                <h6>
                  {list?.is_active ? (
                    <Badge status="success" text="Active" />
                  ) : (
                    <Badge status="error" text="Inactive" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>OTP Verified:</h5>
                <h6>
                  {list?.is_otp_verify ? (
                    <Badge status="success" text="Verified" />
                  ) : (
                    <Badge status="error" text="Not Verified" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>OTP Verified At:</h5>
                <h6>{renderValue("otp_verify_at", list.otp_verify_at)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Added By:</h5>
                <h6>{renderValue("added_by", list.added_by)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Created At:</h5>
                <h6>{renderValue("created_at", list.created_at)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Updated At:</h5>
                <h6>{renderValue("updated_at", list.updated_at)}</h6>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Preferences */}
        <Col span={24} md={12}>
          <Card
            title="User Preferences"
            variant={false}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div className="view-inner-cls">
                <h5>Sound:</h5>
                <h6>
                  {list?.sound ? (
                    <Badge status="success" text="Enabled" />
                  ) : (
                    <Badge status="error" text="Disabled" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Vibration:</h5>
                <h6>
                  {list?.vibration ? (
                    <Badge status="success" text="Enabled" />
                  ) : (
                    <Badge status="error" text="Disabled" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Notifications:</h5>
                <h6>
                  {list?.notification ? (
                    <Badge status="success" text="Enabled" />
                  ) : (
                    <Badge status="error" text="Disabled" />
                  )}
                </h6>
              </div>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <div className="float-right mt-3">
            <Link className="ant-btn ant-btn-secondary" to={`/${routeName}${location.search}`}>
              <ArrowLeftIcon size={15} /> Back
            </Link>
          </div>
        </Col>
      </Row>
    </Spin>
  );
}

export default View;
